import React, { useState, useRef } from "react";
import { Modal, Form, Input, DatePicker, Tour, Space, Button, message } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useCreate } from "../../../../hooks";
import { mawbStockService } from "../../../../api/enhancedApi";

const MawbStockCreateModal = ({ open, onCancel, onSuccess, autoGuide = false }) => {
    const [form] = Form.useForm();
    const [guideOpen, setGuideOpen] = useState(false);

    // 弹窗内字段引导锚点（自包含，DOM 随弹窗渲染，定位时序天然正确）
    const prefixRef = useRef(null);
    const startRef = useRef(null);
    const endRef = useRef(null);
    const dateRef = useRef(null);

    const createMutation = useCreate("mawbStock", mawbStockService.create, {
        onSuccess: (res) => {
            message.success(res?.data?.message || "号段入库成功");
            form.resetFields();
            onSuccess();
        },
    });

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                airline_prefix: values.airline_prefix,
                start_serial: values.start_serial,
                end_serial: values.end_serial,
                received_date: values.received_date.format("YYYY-MM-DD"),
            };
            createMutation.mutate(payload);
        } catch {
            // validation failed
        }
    };

    // 用 Modal 动画结束的原生时机启动引导：此时字段已渲染且位置稳定，定位准确
    const handleAfterOpenChange = (opened) => {
        if (opened) {
            if (autoGuide) setGuideOpen(true);
        } else {
            setGuideOpen(false);
        }
    };

    const guideSteps = [
        {
            title: "航空公司前缀",
            description: "航司的 3 位数字代码，例如 160、999，作为主单号前缀。",
            target: () => prefixRef.current,
        },
        {
            title: "起始序列号",
            description: "号段起始的 7 位流水号，例如 1000000。",
            target: () => startRef.current,
        },
        {
            title: "结束序列号",
            description: "号段结束的 7 位流水号，例如 1000099；系统会生成起止之间的全部主单号。",
            target: () => endRef.current,
        },
        {
            title: "收到日期",
            description: "选择从航司收到该号段的日期。",
            target: () => dateRef.current,
        },
        {
            title: "提交入库",
            description:
                "确认无误后点「确定」，系统会自动生成该范围内的全部主单号，可在详情里逐个查看、分配与收回。",
        },
    ];

    return (
        <Modal
            title={
                <Space>
                    新增号段入库
                    <Button
                        type="text"
                        size="small"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                    >
                        填写引导
                    </Button>
                </Space>
            }
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            confirmLoading={createMutation.isLoading}
            afterOpenChange={handleAfterOpenChange}
            destroyOnClose
            width={520}
        >
            <Form form={form} layout="vertical" preserve={false}>
                <div ref={prefixRef}>
                    <Form.Item
                        name="airline_prefix"
                        label="航空公司前缀"
                        rules={[
                            { required: true, message: "请输入航空公司前缀" },
                            { len: 3, message: "必须为3位数字" },
                            { pattern: /^\d{3}$/, message: "必须为3位数字" },
                        ]}
                    >
                        <Input placeholder="如 160" maxLength={3} />
                    </Form.Item>
                </div>
                <div ref={startRef}>
                    <Form.Item
                        name="start_serial"
                        label="起始序列号"
                        rules={[
                            { required: true, message: "请输入起始序列号" },
                            { len: 7, message: "必须为7位数字" },
                            { pattern: /^\d{7}$/, message: "必须为7位数字" },
                        ]}
                    >
                        <Input placeholder="如 1000000" maxLength={7} />
                    </Form.Item>
                </div>
                <div ref={endRef}>
                    <Form.Item
                        name="end_serial"
                        label="结束序列号"
                        rules={[
                            { required: true, message: "请输入结束序列号" },
                            { len: 7, message: "必须为7位数字" },
                            { pattern: /^\d{7}$/, message: "必须为7位数字" },
                        ]}
                    >
                        <Input placeholder="如 1000099" maxLength={7} />
                    </Form.Item>
                </div>
                <div ref={dateRef}>
                    <Form.Item
                        name="received_date"
                        label="收到日期"
                        rules={[{ required: true, message: "请选择收到日期" }]}
                    >
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                </div>
            </Form>

            <Tour
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                steps={guideSteps}
                zIndex={1100}
            />
        </Modal>
    );
};

export default MawbStockCreateModal;
